import { TokenRequestError } from "./clerk-request"

export interface AccessFailure {
  kind: "authentication" | "denied" | "unavailable"
  code?: string
  retryable: boolean
}

export interface FailedAccess {
  accessFailure: AccessFailure
}

export function isFailedAccess(value: unknown): value is FailedAccess {
  return typeof value === "object" && value !== null && "accessFailure" in value
}

export function classifyAccessFailure(error: unknown): AccessFailure {
  if (error instanceof TokenRequestError) {
    return {
      kind: error.kind === "authentication" ? "authentication" : "unavailable",
      code: error.code,
      retryable: error.kind === "temporary",
    }
  }
  const candidate = error as { status?: number; data?: { code?: string } } | null
  const status = candidate?.status
  const code = candidate?.data?.code
  if (status === 401) return { kind: "authentication", code, retryable: false }
  if (status === 403) return { kind: "denied", code, retryable: false }
  return {
    kind: "unavailable",
    code,
    retryable: typeof status === "number" && [0, 408, 429, 500, 502, 503, 504].includes(status),
  }
}

export function shouldDiscardAccess(error: unknown): boolean {
  return (
    (error instanceof TokenRequestError && error.discardAccess) ||
    !classifyAccessFailure(error).retryable
  )
}

// Clear invalidated data separately from retry classification. Throwing temporary errors
// keeps TanStack's retryer active while the cache contains only the failure state.
export async function loadAccess<T>(
  load: () => Promise<T>,
  discardCachedAccess: (failure: FailedAccess) => void,
): Promise<T | FailedAccess> {
  try {
    return await load()
  } catch (error) {
    const accessFailure = classifyAccessFailure(error)
    if (accessFailure.retryable) {
      if (shouldDiscardAccess(error)) discardCachedAccess({ accessFailure })
      throw error
    }
    return { accessFailure }
  }
}

export function accessRetryDelay(attempt: number, error: unknown): number {
  // Match TanStack Query's browser delay policy, respecting Clerk's Retry-After when supplied.
  const defaultDelay = Math.min(1000 * 2 ** attempt, 30_000)
  const retryAfter = error instanceof TokenRequestError ? error.retryAfterSeconds : undefined
  return typeof retryAfter === "number" && Number.isFinite(retryAfter) && retryAfter >= 0
    ? Math.max(defaultDelay, retryAfter * 1000)
    : defaultDelay
}
